import { Checkbox as MantineCheckbox, CheckboxProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { defaultColorScheme } from '../../../theme/theme';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedCheckboxProps extends CheckboxProps {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  useColorScheme?: boolean; // Use color scheme from state management
}

const Checkbox = forwardRef<HTMLInputElement, ThemedCheckboxProps>(
  ({ gradient = true, gradientVariant = 'primary', useColorScheme = true, styles, ...rest }, ref) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme) {
      const effectiveColorSchemeKey = colorSchemeKey || defaultColorScheme.key;
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === effectiveColorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    if (!gradient) {
      return (
        <MantineCheckbox
          ref={ref}
          styles={{
            ...styles,
            label: {
              ...accessibilityStyles,
              ...(styles as any)?.label,
            },
          }}
          {...rest}
        />
      );
    }

    return (
      <MantineCheckbox
        ref={ref}
        styles={{
          ...styles,
          input: {
            transition: reducedMotion ? 'none' : 'background-color 0.2s ease, border-color 0.2s ease',
            ...(styles as any)?.input,
          },
          label: {
            gap: '8px', // Add gap between checkbox and label
            ...accessibilityStyles,
            ...(styles as any)?.label,
          },
        }}
        // Use Mantine's proper styles API for pseudo-selectors
        sx={(theme) => ({
          input: {
            '&:checked': {
              backgroundColor: `${color} !important`,
              borderColor: `${color} !important`,
            },
            '&:focus': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-within': {
              outline: strongFocus ? `2px solid ${color} !important` : 'none',
            },
          },
        })}
        {...rest}
      />
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
